import React, { useContext, useEffect, useState } from 'react';

import { useTheme } from '@mui/material/styles';
import {
    editionLevels,
    withTemplateCore,
} from '@sunwisesoftware/racking-template-core';
import {
    Box,
    Container as MuiContainer,
    Grid,
} from '@sunwisesoftware/sunwise-ui';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { createStructuredSelector } from 'reselect';

import {
    CollapsedBreadcrumbs,
    HeaderContainer,
    TitleIcon,
} from 'common/components';
import { LoadingContext } from 'common/utils/contexts';
import { getCurrentLanguage } from 'common/utils/helpers/multiregion';
import { getToken } from 'common/utils/helpers/session';

import IcoTemplates from 'resources/icoTemplates.svg?react';

import * as actions from './actions';
import DrawerButtons from './components/DrawerButtons';
import PageContainer from './components/PageContainer';
import PageModal from './components/PageModal';
import PagesDrawer from './components/PagesDrawer';
import SettingsDrawer from './components/SettingsDrawer';
import { handleClonePage } from './helpers';
import * as selectors from './selectors';

const Container = ({
    addBlankPageInTemplate,
    addNewPages,
    customTemplate,
    drawerSize,
    deletePagesInTemplate,
    duplicatePagesInTemplate,
    fetchTemplateCatalogs,
    handleClickBack,
    initialize,
    initTemplate,
    isEmptyPages,
    isInitialize,
    isInitializing,
    isUpdatingCustomTemplate,
    match,
    orderPagesInTemplate,
    reset,
    selectedPageId,
    selectPage,
    addTemplatePages,
    templateTitle,
}) => {
    const { t } = useTranslation();
    const loadingContext = useContext(LoadingContext);
    const theme = useTheme();
    const [newPageModalIsOpen, showNewPageModal] = useState(false);
    const [showPagesDrawer, setShowPagesDrawer] = useState(true);
    const [showSettingsDrawer, setShowSettingsDrawer] = useState(true);
    const templateId = match.params.uid;
    const isDarkMode = theme.palette.mode === 'dark';

    useEffect(() => {
        initialize(templateId, initTemplate);
        return () => {
            reset();
        };
    }, [templateId]);

    useEffect(() => {
        if (isEmpty(loadingContext)) return null;
        if (isUpdatingCustomTemplate)
            loadingContext.openLoading('Guardando cambios'.concat('...'));
        else loadingContext.closeLoading();
    }, [isUpdatingCustomTemplate]);

    if (isInitialize || isInitializing) return null;

    return (
        <>
            <Box display="flex">
                <PagesDrawer
                    deletePagesInTemplate={deletePagesInTemplate}
                    drawerSize={drawerSize}
                    duplicatePagesInTemplate={duplicatePagesInTemplate}
                    isDarkMode={isDarkMode}
                    orderPagesInTemplate={orderPagesInTemplate}
                    selectedPageId={selectedPageId}
                    selectPage={selectPage}
                    setShowPagesDrawer={setShowPagesDrawer}
                    showNewPageModal={showNewPageModal}
                    showPagesDrawer={showPagesDrawer}
                />
                <MuiContainer maxWidth={false} sx={{ p: '0 !important' }}>
                    <HeaderContainer sxHeader={{ px: 2 }}>
                        <Grid size={18}>
                            <TitleIcon
                                icon={<IcoTemplates />}
                                title={t('Template editor')}
                            />
                        </Grid>

                        <Box sx={{ ml: '50px' }}>
                            <CollapsedBreadcrumbs
                                current={templateTitle}
                                locations={[
                                    {
                                        label: 'Plantillas',
                                        action: () => handleClickBack(),
                                    },
                                ]}
                            />
                        </Box>
                    </HeaderContainer>

                    <Box
                        sx={{
                            background: isDarkMode ? '#1b1c1f' : '#f2f4f9',
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            height: {
                                xs: 'calc(100vh - 100px)',
                                md: 'calc(100vh - 92px)',
                            },
                            overflow: 'hidden',
                            position: 'relative',
                        }}
                    >
                        <DrawerButtons
                            setShowPagesDrawer={setShowPagesDrawer}
                            setShowSettingsDrawer={setShowSettingsDrawer}
                            showPagesDrawer={showPagesDrawer}
                            showSettingsDrawer={showSettingsDrawer}
                        />
                        <PageContainer
                            isEmptyPages={isEmptyPages}
                            isInitializing={isInitializing}
                            onAddPage={addBlankPageInTemplate}
                        />
                    </Box>
                    <PageModal
                        handleClickClose={() => showNewPageModal(false)}
                        isOpen={newPageModalIsOpen}
                        onAddPage={addBlankPageInTemplate}
                        onClonePage={handleClonePage(
                            templateId,
                            addTemplatePages,
                            addNewPages,
                        )}
                        onEnter={() => fetchTemplateCatalogs()}
                    />
                </MuiContainer>
                <SettingsDrawer
                    customTemplate={customTemplate}
                    isDarkMode={isDarkMode}
                    setShowSettingsDrawer={setShowSettingsDrawer}
                    showSettingsDrawer={showSettingsDrawer}
                />
            </Box>
        </>
    );
};

const mapStateToProps = createStructuredSelector({
    customTemplate: selectors.getDatafetchTemplate,
    drawerSize: selectors.getDrawerSize,
    isInitialize: selectors.getIsInitialize,
    isUpdatingCustomTemplate: selectors.getIsUpdatingCustomTemplate,
});

const mapDispatchToProps = (dispatch) => ({
    fetchTemplateCatalogs: () => dispatch(actions.fetchTemplateCatalogs()),
    handleClickBack: () => dispatch(actions.handleClickBack()),
    initialize: (id, initTemplate) =>
        dispatch(actions.initialize(id, initTemplate)),
    reset: () => dispatch(actions.reset()),
    addTemplatePages: (customTemplateId, cloneTemplateId, pages, addNewPages) =>
        dispatch(
            actions.addTemplatePages(
                customTemplateId,
                cloneTemplateId,
                pages,
                addNewPages,
            ),
        ),
    updatePage: (templateData) => dispatch(actions.updatePage(templateData)),
    updateTemplate: (templateId, templateData) =>
        dispatch(actions.updateTemplate(templateId, templateData)),
});

Container.propTypes = {
    addBlankPageInTemplate: PropTypes.func,
    addNewPages: PropTypes.func,
    customTemplate: PropTypes.object,
    drawerSize: PropTypes.string,
    deletePagesInTemplate: PropTypes.func,
    duplicatePagesInTemplate: PropTypes.func,
    fetchTemplateCatalogs: PropTypes.func,
    handleClickBack: PropTypes.func,
    initialize: PropTypes.func,
    initTemplate: PropTypes.func,
    isEmptyPages: PropTypes.bool,
    isInitialize: PropTypes.bool,
    isInitializing: PropTypes.bool,
    isUpdatingCustomTemplate: PropTypes.bool,
    match: PropTypes.object,
    orderPagesInTemplate: PropTypes.func,
    reset: PropTypes.func,
    selectedPageId: PropTypes.string,
    selectPage: PropTypes.func,
    addTemplatePages: PropTypes.func,
    templateTitle: PropTypes.string,
};

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    withRouter,
    withTemplateCore((props) => ({
        baseUrl: import.meta.env.VITE_API_URL_PORTAL,
        editionMode: editionLevels.FULL_EDITION_MODE,
        froalaLicenseKey: import.meta.env.VITE_FROALA_LICENSE,
        googleApiKey: import.meta.env.VITE_GOOGLE_MAPS_KEY,
        language: getCurrentLanguage(),
        onChangeInPage: props.updatePage,
        token: getToken(),
        updateTemplatePromise: props.updateTemplate,
    })),
)(Container);
